/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import ptbrLocale from '@fullcalendar/core/locales/pt-br'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import {DateSelectArg} from '@fullcalendar/core'
import interactionPlugin from '@fullcalendar/interaction' // needed for dayClick
import {useQuery, useQueryClient} from 'react-query'
import {Toast} from '../../../../_metronic/partials/widgets'
import {useAuth} from '../../../modules/auth'
import {ProjectCard} from '../../../../_metronic/partials/content/cards/ProjectCard'
import {KTSVG, QUERIES, UserAvatar, escapeHtml} from '../../../../_metronic/helpers'
import {AdjustTimeModal} from '../../../../_metronic/partials/modals/adjust-time/AdjustTimeModal'
import {getDashboardInfoUser, getProjects} from '../dashboard-manager/core/_requests'
import {DashboardMyTasks} from './components/DashboardMyTasks'
import {getTimeTrackingByUserId} from '../../../modules/time-tracking/components/time-tracking-list/core/_requests'
import {User} from '../../../modules/time-tracking/components/time-tracking-list/core/_models'
import {DetailsTimeUserModal} from '../../../../_metronic/partials/modals/details-time-user/DetailsTimeUserModal'
import {TaskEditModal} from '../../../modules/project/components/tasks-list/task-edit-modal/TaskEditModal'
import axios from 'axios'
import ManageCalendarsModal from '../../../modules/calendar/components/ManageCalendarsModal'
import {externalCalendarApiService, ExternalCalendarEvent} from '../../../modules/calendar/services/externalCalendarApi'
import {Dropdown} from 'react-bootstrap'
import {TaskModal} from '../../../../_metronic/partials/modals/task/TaskModal'
import {UserFilter} from '../../../modules/calendar/components/UserFilter'
import AnimatedCounter from '../dashboard-manager/components/AnimatedCounter'
import {DashboardRushMindSection} from './components/DashboardRushMindSection'
import {OnboardingChecklist} from '../../../modules/onboarding/components/OnboardingChecklist'
import {fullCalendarMobileDefaults} from '../../../../_metronic/helpers/fullCalendarDefaults'

const INDICATOR_PERIOD_OPTIONS: {key: string; label: string}[] = [
  {key: '7d', label: 'Últimos 7 dias'},
  {key: '30d', label: 'Últimos 30 dias'},
  {key: 'month', label: 'Este mês'},
  {key: '90d', label: 'Últimos 90 dias'},
  {key: 'year', label: 'Este ano'},
  {key: 'custom', label: 'Personalizado'},
]

const toIsoDate = (date: Date): string => date.toISOString().split('T')[0]

// Traduz a opção escolhida em um intervalo {beginDate, endDate}. Mesmo conjunto de
// "Meu Desempenho". "custom" usa as datas informadas pelo usuário (podem vir vazias).
function computeIndicatorPeriod(
  key: string,
  customBegin: string,
  customEnd: string
): {beginDate: string; endDate: string} {
  const today = new Date()
  const todayIso = toIsoDate(today)
  const daysAgo = (days: number) => toIsoDate(new Date(today.getTime() - days * 24 * 60 * 60 * 1000))

  switch (key) {
    case '7d':
      return {beginDate: daysAgo(6), endDate: todayIso}
    case '30d':
      return {beginDate: daysAgo(29), endDate: todayIso}
    case '90d':
      return {beginDate: daysAgo(89), endDate: todayIso}
    case 'year':
      return {beginDate: toIsoDate(new Date(today.getFullYear(), 0, 1)), endDate: todayIso}
    case 'custom':
      return {beginDate: customBegin, endDate: customEnd}
    case 'month':
    default:
      return {beginDate: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: todayIso}
  }
}

const DashboardUserPage: FC = () => {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showAdjustTimeModal, setShowAdjustTimeModal] = useState<boolean>(false)
  const {currentUser} = useAuth()
  const today = new Date()
  const TemaAtual = window.localStorage.getItem('kt_theme_mode_value') || ''
  const [showModal, setShowModal] = useState<boolean>(false)
  const [dateSelected, setDateSelected] = useState('')
  const [dateFormat, setDateFormatSelected] = useState('')
  const [calendarEvents, setCalendarEvents] = useState<any[]>([])
  const queryClient = useQueryClient()
  const [calendarKey, setCalendarKey] = useState(0)
  const [showManageCalendarsModal, setShowManageCalendarsModal] = useState(false)
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  const [initialTaskName, setInitialTaskName] = useState<string>('')
  const [initialTaskDate, setInitialTaskDate] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  // Filtro de período dos indicadores de tarefas. Afeta só os dois cards de entrega
  // ("no prazo" / "com atraso"); "abertas para mim" segue sendo o retrato do agora.
  const indPeriodKey = searchParams.get('indPeriod') || 'month'
  const indCustomBegin = searchParams.get('indBegin') || ''
  const indCustomEnd = searchParams.get('indEnd') || ''

  const indPeriod = useMemo(
    () => computeIndicatorPeriod(indPeriodKey, indCustomBegin, indCustomEnd),
    [indPeriodKey, indCustomBegin, indCustomEnd]
  )

  const handleIndPeriodChange = (key: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('indPeriod', key)
    if (key !== 'custom') {
      next.delete('indBegin')
      next.delete('indEnd')
    }
    setSearchParams(next, {replace: true})
  }

  const handleIndCustomDate = (field: 'indBegin' | 'indEnd', value: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('indPeriod', 'custom')
    if (value) {
      next.set(field, value)
    } else {
      next.delete(field)
    }
    setSearchParams(next, {replace: true})
  }

  // Deep-link dos cards de entrega para a lista filtrada em Minhas Tarefas.
  const deliveryTasksLink = (punctuality: 'on_time' | 'late'): string => {
    const params = new URLSearchParams({status: 'fechada', punctuality})
    if (indPeriod.beginDate) params.set('endFrom', indPeriod.beginDate)
    if (indPeriod.endDate) params.set('endTo', indPeriod.endDate)
    return `/mytasks?${params.toString()}`
  }

  // Função para converter data YYYY-MM-DD para YYYY-MM-DD (manter formato ISO)
  const convertDateFormatToBR = (dateString: string): string => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return ''
    }
    // Usar a string diretamente para evitar problemas de fuso horário
    // dateString já vem no formato YYYY-MM-DD do FullCalendar
    return dateString
  }

  // Função para atualizar expected_date
  const updateTaskExpectedDate = async (taskId: string, newDate: string) => {
    try {
      // Remover o prefixo "task-" do taskId se existir
      const cleanTaskId = taskId.startsWith('task-') ? taskId.substring(5) : taskId
      
      const url = `${process.env.REACT_APP_THEME_API_URL}/api/tarefas/calendar/${cleanTaskId}`
      const postData = {
        expected_date: newDate
      }
      await axios.put(url, postData)
      
      // Invalidar cache para recarregar os dados
      queryClient.invalidateQueries(['dashboard-tasks'])
      
      return true
    } catch (error) {
      console.error('Erro ao atualizar data esperada da tarefa:', error)
      throw error
    }
  }

  // Handler para quando um evento é movido (drag and drop)
  const handleEventDrop = async (dropInfo: any) => {
    const eventId = dropInfo.event.id
    
    // TODO: Futuramente reativar verificação de administrador
    // Verificar se o usuário tem permissão para mover tarefas
    // if (!currentUser?.administrator) {
    //   dropInfo.revert()
    //   return
    // }
    
    // Verificar se é uma tarefa (não feriado ou férias)
    if (eventId.startsWith('holiday-') || eventId.startsWith('vacation-') || eventId.startsWith('fallback-')) {
      // Reverter a mudança para feriados, férias e eventos de fallback
      dropInfo.revert()
      return
    }

    // Verificar se a nova data não é no passado (permitir data atual)
    const newDateStr = dropInfo.event.startStr // Formato: YYYY-MM-DD
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0] // Formato: YYYY-MM-DD
    
    if (newDateStr < todayStr) {
      // Reverter a mudança para datas passadas
      dropInfo.revert()
      Toast.fire({
        icon: 'warning',
        title: 'Não é possível mover tarefas para datas passadas.'
      })
      return
    }

    // Verificar se a tarefa está concluída
    const task = fetchedTasks.find((t: any) => String(t.id) === eventId)
    if (task && (task.status === 'Entregue' || task.status === 'Concluída')) {
      // Reverter a mudança para tarefas concluídas
      dropInfo.revert()
      alert('Tarefas concluídas não podem ser movidas.')
      return
    }

    try {
      // Converter a nova data para o formato esperado pela API (YYYY-MM-DD)
      const formattedDate = convertDateFormatToBR(dropInfo.event.startStr)
      
      // Atualizar a data esperada da tarefa
      await updateTaskExpectedDate(eventId, formattedDate)
      
    } catch (error) {
      // Reverter a mudança em caso de erro
      dropInfo.revert()
      console.error('Erro ao atualizar data da tarefa:', error)
      alert('Erro ao atualizar a data da tarefa. Tente novamente.')
    }
  }

  const {
    isLoading: isDashboardLoading,
    data: dashboardInfo,
    refetch,
    error,
  } = useQuery(
    [QUERIES.DASHBOARD_INFO_USER, indPeriod.beginDate, indPeriod.endDate],
    () => {
      return getDashboardInfoUser({
        beginDate: indPeriod.beginDate || undefined,
        endDate: indPeriod.endDate || undefined,
      })
    },
    {
      cacheTime: 0,
      keepPreviousData: true,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: isProjectsLoading,
    data: dashboardProjects,
    refetch: projectsRefetch,
    error: errorRefetch,
  } = useQuery(
    `${QUERIES.DASHBOARD_PROJECTS}`,
    () => {
      return getProjects()
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  const {
    isLoading: isTimeTrackingLoading,
    data: dashboardTimeTracking,
    refetch: timetrackingRefetch,
    error: errorRefetchTimeTracking,
  } = useQuery(
    `${QUERIES.DASHBOARD_TIMETRACKING}`,
    () => {
      return getTimeTrackingByUserId(currentUser?.id)
    },
    {
      cacheTime: 0,
      onError: (err) => {
        console.error(err)
      },
      refetchOnWindowFocus: false,
    }
  )

  // Buscar tarefas com data de entrega na semana atual
  const startOfWeek = new Date(today)
  startOfWeek.setDate(today.getDate() - today.getDay())
  const endOfWeek = new Date(today)
  endOfWeek.setDate(today.getDate() + (6 - today.getDay()))
  

  const {data: fetchedTasks = [], isLoading: isLoadingCalendarTasks} = useQuery({
    queryKey: ['dashboard-tasks', startOfWeek.toISOString().split('T')[0], endOfWeek.toISOString().split('T')[0], selectedUserId],
    queryFn: () => {
      let url = `${process.env.REACT_APP_THEME_API_URL}/api/tarefas/calendar/${
        startOfWeek.toISOString().split('T')[0]
      }/${endOfWeek.toISOString().split('T')[0]}`
      if (selectedUserId) {
        url += `?user_id=${selectedUserId}`
      }
      return axios.get(url).then((res) => res.data.data)
    },
    refetchOnWindowFocus: true,
  })

  // useQuery para eventos externos (iCal) - apenas semana atual
  const {data: externalEvents = [], isLoading: isLoadingExternalEvents} = useQuery({
    queryKey: ['dashboard-external-calendar-events', startOfWeek.toISOString().split('T')[0], endOfWeek.toISOString().split('T')[0]],
    queryFn: () =>
      startOfWeek && endOfWeek
        ? externalCalendarApiService.getEvents(
            startOfWeek.toISOString().split('T')[0],
            endOfWeek.toISOString().split('T')[0]
          )
        : Promise.resolve([]),
    enabled: !!startOfWeek && !!endOfWeek,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Considerar dados válidos por 5 minutos
  })

  // useQuery para feriados
  // OTIMIZAÇÃO: Usar useQuery evita múltiplas requisições para api/holiday/
  // quando as dependências de fetchCalendarEvents mudam.
  // O cache é gerenciado automaticamente pelo React Query.
  const {data: holidays = [], isLoading: isLoadingHolidays} = useQuery({
    queryKey: ['dashboard-holidays'],
    queryFn: async () => {
      const url = `${process.env.REACT_APP_THEME_API_URL}/api/holiday/`
      const response = await axios.get(url)
      return response.data.data || []
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Considerar dados válidos por 5 minutos
  })

  // "Carregando" só enquanto as 3 fontes do calendário respondem. Calendário sem eventos depois disso
  // é estado vazio (conta nova), não carregamento.
  const isCalendarLoading = isLoadingCalendarTasks || isLoadingExternalEvents || isLoadingHolidays
  const [calendarLoadFailed, setCalendarLoadFailed] = useState(false)

  // Callback para quando um calendário externo for adicionado/atualizado
  const handleCalendarUpdated = useCallback(() => {
    queryClient.invalidateQueries(['dashboard-external-calendar-events'])
    // Também invalidar o cache geral de eventos externos
    queryClient.invalidateQueries(['external-calendar-events'])
  }, [queryClient])

  const [data, setData] = useState<User>()
  useEffect(() => {
    dashboardTimeTracking && setData(dashboardTimeTracking)
  }, [isTimeTrackingLoading])

  let perfomanceLevel = ''
  if (
    (data?.performance! >= data?.instanceConfiguration?.min_hour_index! &&
      data?.performance! < data?.instanceConfiguration?.max_hour_index!) ||
    !data?.has_time_tracking
  ) {
    perfomanceLevel = 'text-success fw-bold'
  } else if (data?.performance! > data?.instanceConfiguration?.max_hour_index!) {
    perfomanceLevel = 'text-warning fw-bold'
  } else {
    perfomanceLevel = 'text-danger fw-bold'
  }

  let performace

  if (data?.performance === undefined) {
    performace = 0
  } else {
    performace = data.performance
  }

  const openDetails = (dateSelected: string, dateFormat: string) => {
    //Abre modal para mostrar o detalhamento dos horários para aquele usuário e dia
    setDateSelected(dateSelected)
    setDateFormatSelected(dateFormat)
    setShowModal(true)
  }

  // const toolbar = {
  //   left: 'prevYear,prev,next,nextYear today',
  //   center: 'title',
  //   right: 'dayGridMonth,dayGridWeek,dayGridDay',
  // }
  let eventGuid = 0
  const createEventId = () => String(eventGuid++)

  const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
    let title = prompt('Insira o título do evento')?.trim()
    let calendarApi = selectInfo.view.calendar
    calendarApi.unselect()
    if (title) {
      calendarApi.addEvent({
        id: createEventId(),
        title,
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay,
      })
    }
  }, [])

  const handleEventClick = useCallback(
    (clickInfo: any) => {
      const eventId = clickInfo.event.id
      const eventTitle = clickInfo.event.title
      const isExternal = clickInfo.event.extendedProps?.external
      
      // Não faz nada para feriados ou férias
      if (eventId.startsWith('holiday-') || eventId.startsWith('vacation-')) {
        return
      }
      
      // Se for evento externo, abrir modal de criação de tarefa com título e data pré-preenchidos
      if (isExternal || eventId.startsWith('external_')) {
        setInitialTaskName(eventTitle)
        const eventDate = clickInfo.event.startStr.split('T')[0]
        setInitialTaskDate(eventDate)
        setShowCreateTaskModal(true)
        return
      }
      
      // Se for tarefa normal, abrir modal de edição
      if (clickInfo.event.extendedProps.taskId) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('taskId', clickInfo.event.extendedProps.taskId.toString())
        setSearchParams(newParams)
      }
    },
    [searchParams, setSearchParams]
  )

  // Função para buscar eventos reais (feriados e outros eventos)
  const fetchCalendarEvents = useCallback(async () => {
    try {
      // Converter feriados para formato do FullCalendar
      const holidayEvents = holidays.map((holiday: any) => {
        // Converter data do formato DD/MM/YYYY para YYYY-MM-DD
        let startDate = holiday.date
        if (holiday.date && typeof holiday.date === 'string') {
          if (holiday.date.includes('/')) {
            // Se está no formato DD/MM/YYYY, converter para YYYY-MM-DD
            const parts = holiday.date.split('/')
            if (parts.length === 3) {
              startDate = `${parts[2]}-${parts[1]}-${parts[0]}`
            }
          } else if (holiday.date.includes('T')) {
            // Se tem hora, remover a parte da hora
            startDate = holiday.date.split('T')[0]
          }
        }

        return {
          id: `holiday-${holiday.id}`,
          title: holiday.name || 'Feriado',
          start: startDate,
          allDay: true,
          classNames: ['holiday-event'],
          display: 'background', // Feriados como background
          backgroundColor: '#efa7a7',
          borderColor: '#efa7a7',
          editable: false, // Não permitir arrastar feriados
          extendedProps: {
            type: 'holiday',
            holidayId: holiday.id,
          },
        }
      })

      // Filtrar tarefas entregues/concluídas (não devem aparecer no dashboard)
      const tasks = fetchedTasks.filter((task: any) => {
        const isCompleted = task.status === 'Entregue' || task.status === 'Concluída'
        return !isCompleted
      })

      const taskEvents = tasks.map((task: any) => {
        let startDate = task.expected_date
        if (task.expected_date && typeof task.expected_date === 'string') {
          if (task.expected_date.includes('/')) {
            const parts = task.expected_date.split('/')
            if (parts.length === 3) {
              startDate = `${parts[2]}-${parts[1]}-${parts[0]}`
            }
          } else if (task.expected_date.includes('T')) {
            startDate = task.expected_date.split('T')[0]
          }
        }

        // Verificar se a tarefa está atrasada
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        const eventDate = new Date(startDate)
        const isOverdue = eventDate < yesterday
        const isInProgress = task.status === 'Em andamento'

        let eventClass = 'normal-task'
        if (isOverdue) {
          eventClass = 'overdue-task'
        } else if (isInProgress) {
          eventClass = 'in-progress-task'
        } else if (task.status === 'Entregue' || task.status === 'Concluída') {
          eventClass = 'completed-task'
        }

        // Buscar informações do cliente e projeto
        const clientName = task.clientName || 'Cliente não definido'
        const projectName = task.project_name || 'Projeto não definido'

        // Formatar o título com tarefa, cliente e projeto
        // Nome, cliente e projeto vêm do usuário e o título vai para innerHTML: sempre escapar
        const htmlTitle = `<strong>${escapeHtml(task.name)}</strong><br/>${escapeHtml(clientName)} / ${escapeHtml(projectName)}`

        return {
          id: `task-${task.id}`,
          title: '', // Título vazio para evitar que o modal leia
          htmlTitle: htmlTitle, // Título com HTML para exibição
          start: startDate,
          allDay: true,
          classNames: [eventClass],
          editable: !(task.status === 'Entregue' || task.status === 'Concluída'), // Será controlado pelo FullCalendar
          extendedProps: {
            type: 'task',
            taskId: task.id,
          },
        }
      })

      // Converte eventos externos para formato FullCalendar
      const externalCalendarEvents = externalEvents.map((event: ExternalCalendarEvent) => ({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        allDay: event.allDay,
        color: event.color,
        classNames: ['external-event'],
        editable: false,
        extendedProps: {
          calendarName: event.calendarName,
          calendarId: event.calendarId,
          external: true,
          description: event.description,
          location: event.location
        }
      }))

      const allEvents = [...holidayEvents, ...taskEvents, ...externalCalendarEvents]
      setCalendarEvents(allEvents)
      setCalendarLoadFailed(false)
    } catch (error) {
      console.error('Erro ao buscar eventos do calendário:', error)
      // Não inventar eventos de exemplo: mostra o calendário vazio com um aviso.
      setCalendarEvents([])
      setCalendarLoadFailed(true)
    }
  }, [fetchedTasks, currentUser, externalEvents, holidays])

  // Carregar eventos quando o componente montar
  useEffect(() => {
    fetchCalendarEvents()
  }, [fetchCalendarEvents])

  // Efeito para forçar re-renderização do calendário quando os dados mudarem
  useEffect(() => {
    setCalendarKey(prev => prev + 1)
  }, [fetchedTasks])

  // Listener para mudanças nas queries do calendário
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event && event.type === 'queryUpdated' && event.query.queryKey[0] === 'dashboard-tasks') {
        setCalendarKey(prev => prev + 1)
      }
    })
    
    return () => unsubscribe()
  }, [queryClient])

  return (
    <>
      {/* START HEADER */}
      <div className='container'>
        <div className='dash-section'>
          <div className='row align-items-center g-4'>
            <div className='col-12 col-lg-7'>
              <div className='dash-overline mb-2'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.WORKPLACE'})} ·{' '}
                {intl.formatMessage({id: 'DASHBOARD.LABEL.FEATURED_OF_THE_DAY'})}
              </div>
              <div className='d-flex align-items-center'>
                <div className='me-3'>
                  <UserAvatar
                    name={currentUser?.name ?? ''}
                    avatar={currentUser?.avatar}
                    sizeClass='symbol-45px'
                    className='overflow-hidden'
                    imgClassName='object-fit-cover'
                  />
                </div>
                <p className='text-gray-900 fw-bold fs-4 mb-0'>
                  {today.getHours() < 12
                    ? intl.formatMessage({id: 'GENERAL.GOOD_MORNING'})
                    : today.getHours() < 18
                    ? intl.formatMessage({id: 'GENERAL.GOOD_AFTERNOON'})
                    : intl.formatMessage({id: 'GENERAL.GOOD_EVENING'})}
                  , {currentUser?.name}!
                </p>
              </div>
            </div>
            <div className='col-12 col-lg-5'>
              <div className='row g-3 justify-content-lg-end'>
                <div className='col-6 col-sm-auto'>
                  <Link to='/projects' className='dash-stat dash-stat-link d-block'>
                    <span className='dash-stat-label'>
                      {intl.formatMessage({id: 'DASHBOARD.LABEL.PROJECTS_I_AM_IN'})}
                    </span>
                    <AnimatedCounter
                      value={dashboardInfo?.projects_quantity ?? 0}
                      duration={1200}
                      className='dash-stat-value text-gray-900'
                    />
                  </Link>
                </div>
                <div className='col-6 col-sm-auto'>
                  <Link
                    to='/mytasks?overdue=true'
                    className='dash-stat dash-stat-link d-block'
                  >
                    <span className='dash-stat-label'>
                      {intl.formatMessage({id: 'DASHBOARD.LABEL.LATE_TASKS'})}
                    </span>
                    <AnimatedCounter
                      value={dashboardInfo?.user_late_tasks_count ?? 0}
                      duration={1200}
                      className={`dash-stat-value ${
                        (dashboardInfo?.user_late_tasks_count ?? 0) > 0
                          ? 'text-danger'
                          : 'text-gray-900'
                      }`}
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* END HEADER */}
      <div className='container pt-8 pb-15'>
        <div className='row g-4 mb-6'>
          <div className='col-12 col-xl-6'>
           <div className='dash-section h-100 mb-0 d-flex flex-column'>
            <div className='dash-section-head'>
              <h2 className='dash-section-title'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.TASKS_INDICATORS'})}
              </h2>
              <select
                className='form-select form-select-sm w-auto'
                value={indPeriodKey}
                onChange={(e) => handleIndPeriodChange(e.target.value)}
                title='Filtra os cards de entregas por período'
                aria-label='Período dos indicadores de entrega'
              >
                {INDICATOR_PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            {indPeriodKey === 'custom' && (
              <div className='d-flex flex-wrap gap-3 mb-4'>
                <input
                  type='date'
                  className='form-control form-control-sm w-auto'
                  value={indCustomBegin}
                  max={indCustomEnd || undefined}
                  onChange={(e) => handleIndCustomDate('indBegin', e.target.value)}
                />
                <input
                  type='date'
                  className='form-control form-control-sm w-auto'
                  value={indCustomEnd}
                  min={indCustomBegin || undefined}
                  onChange={(e) => handleIndCustomDate('indEnd', e.target.value)}
                />
              </div>
            )}
            <div className='row g-3 flex-grow-1'>
              <div className='col-12 col-sm-4'>
                <Link
                  to='/mytasks'
                  className='dashboard-indicator-card rounded-2 px-4 py-4 h-100'
                  title='Tarefas abertas atribuídas a mim'
                >
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <KTSVG
                      path='media/icons/duotune/general/gen013.svg'
                      className='svg-icon svg-icon-2 svg-icon-primary'
                    />
                    <i className='bi bi-arrow-right dashboard-indicator-arrow text-brand fs-5' />
                  </div>
                  <div className='m-0'>
                    <span className='text-gray-900 fw-bolder d-block fs-2x lh-1 mb-1'>
                      {dashboardInfo?.userTasksOpenQuantity ?? 0}
                    </span>
                    <span className='text-gray-700 fw-semibold fs-6 d-block'>
                      {intl.formatMessage({id: 'DASHBOARD.LABEL.OPEN_TO_ME'})}
                    </span>
                  </div>
                </Link>
              </div>
              <div className='col-12 col-sm-4'>
                <Link
                  to={deliveryTasksLink('on_time')}
                  className='dashboard-indicator-card rounded-2 px-4 py-4 h-100'
                  title='Tarefas que entreguei dentro do prazo no período'
                >
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <KTSVG
                      path='media/icons/duotune/general/gen020.svg'
                      className='svg-icon svg-icon-2 svg-icon-primary'
                    />
                    <i className='bi bi-arrow-right dashboard-indicator-arrow text-brand fs-5' />
                  </div>
                  <div className='m-0'>
                    <span className='text-gray-900 fw-bolder d-block fs-2x lh-1 mb-1'>
                      {dashboardInfo?.userTaskDeliveryQuantity ?? 0}
                    </span>
                    <span className='text-gray-700 fw-semibold fs-6 d-block'>
                      {intl.formatMessage({id: 'DASHBOARD.LABEL.FINISHED_ON_TIME'})}
                    </span>
                  </div>
                </Link>
              </div>
              <div className='col-12 col-sm-4'>
                <Link
                  to={deliveryTasksLink('late')}
                  className='dashboard-indicator-card rounded-2 px-4 py-4 h-100'
                  title='Tarefas que entreguei com atraso no período'
                >
                  <div className='d-flex justify-content-between align-items-center mb-4'>
                    <KTSVG
                      path='media/icons/duotune/general/gen040.svg'
                      className='svg-icon svg-icon-2 svg-icon-primary'
                    />
                    <i className='bi bi-arrow-right dashboard-indicator-arrow text-brand fs-5' />
                  </div>
                  <div className='m-0'>
                    <span className='text-gray-900 fw-bolder d-block fs-2x lh-1 mb-1'>
                      {dashboardInfo?.userTaskNotDeliveryQuantity ?? 0}
                    </span>
                    <span className='text-gray-700 fw-semibold fs-6 d-block'>
                      {intl.formatMessage({id: 'DASHBOARD.LABEL.NOT_FINISHED'})}
                    </span>
                  </div>
                </Link>
              </div>
            </div>
            <p className='text-gray-700 fs-6 mt-4 mb-0'>
              "Abertas para mim" mostra o total atual. Os dois cards de entrega seguem o período
              selecionado acima.
            </p>
           </div>
          </div>

          {/* Painel: horas lançadas em tarefas */}
          <div className='col-12 col-xl-6'>
           <div className='dash-section h-100 mb-0 d-flex flex-column'>
            <div className='dash-section-head'>
              <h2 className='dash-section-title'>
                {intl.formatMessage({id: 'DASHBOARD.LABEL.REGISTERED_HOURS_IN_TASKS'})}
              </h2>
            </div>

            <div className='d-flex overflow-auto pb-2 flex-grow-1'>
              {data?.hours_by_day?.length == 0 ? (
                <p className='text-gray-600 fs-6 mb-0'>
                  Nenhum apontamento de horas ainda. Registre suas horas nas tarefas para
                  acompanhar sua jornada aqui.
                </p>
              ) : (
                data?.hours_by_day!.map((row) => {
                  return (
                    <div
                      key={row.date}
                      className='dashboard-hours-day cursor-pointer d-flex flex-column align-items-center flex-shrink-0 mx-2 mb-6'
                      onClick={() => openDetails(row.date, row.date_format)}
                    >
                        <span
                          className='fs-7 fw-semibold text-truncate w-100 text-center text-gray-700'
                          title={
                            row.is_holiday
                              ? row.holiday
                              : row.is_vacation_day
                              ? 'Férias'
                              : `${row.day} - ${row.day_name}`
                          }
                        >
                          {row.day} - {row.day_name}
                          {row.has_change ? (
                            <i
                              className='fas fa-exclamation-circle fs-8 ps-1'
                              data-bs-toggle='tooltip'
                              title='Alteração de jornada de trabalho.'
                            ></i>
                          ) : null}
                        </span>
                        <div
                          className='w-55px h-75px bg-gray-300 position-relative d-flex align-items-end rounded overflow-hidden'
                          // className='w-55px h-75px bg-gray-300 position-relative d-flex align-items-end rounded'
                          data-bs-toggle='tooltip'
                          title={row.tooltip}
                        >
                          <div
                            className={`
                      ${
                        !row.is_working_day || row.is_holiday || row.is_vacation_day
                          ? 'bg-brand-progress'
                          : row.total_time_porcent >=
                              data?.instanceConfiguration?.min_hour_index! &&
                            row.total_time_porcent <= data?.instanceConfiguration?.max_hour_index!
                          ? 'bg-success'
                          : row.total_time_porcent < data?.instanceConfiguration?.min_hour_index!
                          ? 'bg-danger'
                          : 'bg-warning'
                      }
                        rounded w-80px
                        `}
                            role='progressbar'
                            style={{
                              height:
                                row.is_holiday || row.is_vacation_day
                                  ? '100%'
                                  : `${row.total_time_porcent}%`,
                            }}
                          ></div>
                          <div className='text-center text-white position-absolute end-0 bottom-0px start-0 pb-1'>
                            {row.total_time}h
                          </div>
                        </div>
                      </div>
                  )
                })
              )}
            </div>
            <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4'>
              <div className='d-flex align-items-center'>
                <div className='text-gray-600 fs-4 fw-bold me-5'>
                  <span className={perfomanceLevel}>{data?.hours_tracked}h</span> de{' '}
                  {data?.hours_total}
                </div>
                <div className={perfomanceLevel}>{performace}%</div>
              </div>
              <Link
                to={'../time-tracking-user'}
                className='btn btn-light btn-sm w-fit-content py-2 px-3'
              >
                {intl.formatMessage({id: 'DASHBOARD.LABEL.SEE_MY_HISTORIC'})}
              </Link>
            </div>
            <DetailsTimeUserModal
              show={showModal}
              dateSelected={dateSelected}
              dateFormatSelected={dateFormat}
              userId={currentUser?.id.toString()!}
              handleClose={() => setShowModal(false)}
            />
           </div>
          </div>
        </div>

        <OnboardingChecklist />

        {/* Bloco RushMind: score de engajamento + cobranças abertas do colaborador */}
        {!currentUser?.external_user && <DashboardRushMindSection />}

        {/* Calendário Semanal */}
        <div className='dash-section'>
          <div className='dash-section-head'>
            <h2 className='dash-section-title'>Minha agenda da semana</h2>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Filtro de usuários - somente para administradores e gestores */}
              {Boolean(currentUser?.administrator || currentUser?.manager) && (
                <div className='me-3'>
                  <UserFilter 
                    selectedUserId={selectedUserId} 
                    onUserChange={(userId) => setSelectedUserId(userId)}
                    compact={true}
                  />
                </div>
              )}
              
              {/* Indicador de loading para eventos externos */}
              {isLoadingExternalEvents && (
                <div className="me-3 d-flex align-items-center text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <small>Sincronizando calendários...</small>
                </div>
              )}
              
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  size="sm"
                  className="btn btn-sm"
                >
                  <i className="bi bi-calendar-plus me-2"></i>
                  Adicionar Agenda
                </Dropdown.Toggle>
                
                <Dropdown.Menu>
                  {/* Calendário iCal */}
                  <Dropdown.Item onClick={() => setShowManageCalendarsModal(true)}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-calendar-plus fs-4 text-brand me-3"></i>
                      <div className="flex-grow-1">
                        <div className="fw-bold">Gerenciar Calendários</div>
                        <div className="text-muted fs-7">Adicionar, remover ou editar</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* CSS para estilização dos eventos */}

          <div
            className="calendar-container"
          >
            {isCalendarLoading && calendarEvents.length === 0 && (
              <div className='text-center py-5'>
                <div className='spinner-border spinner-border-sm text-gray-500' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
                <p className='mt-2 text-gray-600'>Carregando eventos do calendário...</p>
              </div>
            )}

            {!isCalendarLoading && calendarLoadFailed && (
              <div className='dash-empty-text text-center py-3'>
                Não foi possível carregar os eventos agora. Atualize a página para tentar de novo.
              </div>
            )}

            {!isCalendarLoading && !calendarLoadFailed && calendarEvents.length === 0 && (
              <div className='dash-empty-text text-center py-3'>
                Nenhum evento nesta semana. Tarefas com prazo, feriados e suas agendas externas
                aparecem aqui.
              </div>
            )}

            <FullCalendar
              key={calendarKey}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView='dayGridWeek'
              locale={ptbrLocale}
              selectable={false}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              editable={true}
              headerToolbar={false}
              height='300px'
              events={calendarEvents}
              eventDisplay='block'
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: false,
                hour12: false
              }}
              {...fullCalendarMobileDefaults()}
              eventDidMount={(info) => {
                const titleElement = info.el.querySelector('.fc-event-title')
                if (titleElement && info.event.extendedProps.htmlTitle) {
                  titleElement.innerHTML = info.event.extendedProps.htmlTitle
                }
                
                // Limpar formatação duplicada dos eventos externos (manter formato HH:MM)
                const timeElement = info.el.querySelector('.fc-event-time') as HTMLElement
                if (timeElement && info.event.extendedProps?.external) {
                  const timeText = timeElement.textContent || ''
                  
                  // Se tem "h" duplicado, limpar e manter apenas o formato HH:MM
                  if (timeText.includes('h') && timeText.length > 6) {
                    const match = timeText.match(/^(\d{2}:\d{2})/)
                    if (match) {
                      timeElement.textContent = match[1]
                    }
                  }
                }
                
                // Adicionar cursor pointer para eventos externos
                if (info.event.extendedProps?.external) {
                  info.el.style.cursor = 'pointer'
                }
                
                // Configurar atributos de arrastar
                if (info.event.extendedProps.editable !== undefined) {
                  info.el.setAttribute('data-editable', info.event.extendedProps.editable.toString())
                }
              }}
            />

            {/* Legenda do Calendário */}
            <div className='d-flex flex-wrap align-items-center gap-3 mt-4 fs-7 text-gray-700'>
              <div className='d-flex align-items-center'>
                <div className='legend-item overdue-task me-2'></div>
                <span>Vencidas</span>
              </div>
              <div className='d-flex align-items-center'>
                <div className='legend-item in-progress-task me-2'></div>
                <span>Em andamento</span>
              </div>
              <div className='d-flex align-items-center'>
                <div className='legend-item normal-task me-2'></div>
                <span>Backlog</span>
              </div>
              <div className='d-flex align-items-center'>
                <div className='legend-item holiday-event me-2'></div>
                <span>Feriados</span>
              </div>
              <div className='d-flex align-items-center'>
                <div className='legend-item external-event me-2'></div>
                <span>Calendários externos</span>
              </div>
            </div>
          </div>
        </div>
        <DashboardMyTasks />
        {dashboardProjects?.projects && dashboardProjects.projects.length > 0 && (
        <div className='dash-section'>
          <div className='dash-section-head'>
            <h2 className='dash-section-title'>
              {intl.formatMessage({id: 'DASHBOARD.LABEL.PROJECTS_I_LEAD'})}
            </h2>
          </div>

          <div className='row g-4'>
          {dashboardProjects.projects.map((project, i) => (
            <div key={i} className='col-md-6 col-xl-4'>
              <ProjectCard
                id={parseInt(project.id!)}
                name={project.name}
                client={project.clientName}
                budgetedHours={project.budgeted_hours}
                users={project.users!}
                begin_date={project.begin_date}
                end_date={project.end_date}
                closed_at={project.closed_at}
                totalTime={project.totalTime}
                profit_margin={project.profit_margin}
                is_private={project.is_private}
                criticality_score={project.criticality_score}
                critical_tasks_count={project.critical_tasks_count}
                late_tasks_count={project.late_tasks_count}
                high_priority_tasks_count={project.high_priority_tasks_count}
                overdue_1_3_tasks_count={project.overdue_1_3_tasks_count}
                overdue_4_7_tasks_count={project.overdue_4_7_tasks_count}
                overdue_8_plus_tasks_count={project.overdue_8_plus_tasks_count}
                due_soon_high_critical_tasks_count={project.due_soon_high_critical_tasks_count}
                total_tasks={project.total_tasks}
              />
            </div>
          ))}
          </div>
        </div>
        )}
      </div>
      <AdjustTimeModal
        show={showAdjustTimeModal}
        handleClose={() => setShowAdjustTimeModal(false)}
      />

      <ManageCalendarsModal
        show={showManageCalendarsModal}
        onClose={() => setShowManageCalendarsModal(false)}
        onSuccess={handleCalendarUpdated}
      />

      <TaskModal
        show={showCreateTaskModal}
        handleClose={() => {
          setShowCreateTaskModal(false)
          setInitialTaskName('')
          setInitialTaskDate('')
        }}
        initialTaskName={initialTaskName}
        initialTaskDate={initialTaskDate}
      />
    </>
  )
}

export {DashboardUserPage}
